import React from "react";
import { createPortal } from "react-dom";
import CONFIG from "config/config";
import { useIsMobileViewport } from "./useIsMobileViewport";

/**
 * Hard mobile blocker for the flyer builder.
 *
 * The Creative Studio canvas + sidebars don't fit (or function) below
 * ~900px viewport width — tool palette, settings drawer, alignment
 * panel and the canvas itself all rely on multi-pane desktop layouts.
 * Rather than ship a broken editor on phones, we put a full-screen
 * blocker over the page and offer a one-click escape back to the
 * Oppizi platform home.
 *
 * Distinguishing this from `BrowserWarningModal`:
 *   - That one is a SOFT nag (Continue / Get Chrome). Users can stay.
 *   - This one is a HARD block. Backdrop clicks and Escape do
 *     nothing; the only action is the "Back to platform" button.
 *
 * Lives below the BrowserWarning so when someone opens the builder
 * on Safari Mobile they see the mobile blocker (the more actionable
 * problem), not the browser nag.
 *
 * Reactive to resize: viewport detection runs through a hook that
 * listens for resize / orientationchange events. If a tablet user
 * rotates from portrait to landscape and crosses 900px, the blocker
 * vanishes mid-session.
 */

const FONT_STACK = '"Inter", "Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const MobileBlockerModal: React.FC = () => {
  const isMobile = useIsMobileViewport(900);

  if (!isMobile) return null;

  const handleBackToPlatform = () => {
    // Send the user to the platform home. `PLATFORM_BASE_URL` is
    // configured per-env in `src/config/config.js`; we replace the
    // whole window so the builder unmounts cleanly (no half-loaded
    // canvas left behind in memory).
    if (typeof window !== "undefined") {
      window.location.href = CONFIG.PLATFORM_BASE_URL || "/";
    }
  };

  const modal = (
    <div
      // No backdrop dismissal — this is a hard block. We still keep
      // the click handler off to short-circuit any propagation from
      // the card's children.
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3001, // one above BrowserWarning (3000)
        background: "rgba(0, 8, 30, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: FONT_STACK,
      }}
    >
      {/* Same card chrome as BrowserWarningModal so the two read as a
          family. We scope the width down on tiny phones via the
          max-width so the card never overflows. */}
      <div
        style={{
          display: "flex",
          width: 555,
          maxWidth: "100%",
          padding: "24px 24px 32px 24px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 24,
          borderRadius: 8,
          border: "1px solid #E2E2FC",
          background: "#FFF",
          boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.10)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFeatureSettings: '"liga" off, "clig" off',
              fontFamily: FONT_STACK,
              fontSize: 24,
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            Available on desktop only
          </h2>
          <p
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFeatureSettings: '"liga" off, "clig" off',
              fontFamily: FONT_STACK,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            The Creative Studio needs a larger screen to edit your design. Switch to a computer or a tablet in landscape
            to continue.
          </p>
        </div>

        <button
          type="button"
          onClick={handleBackToPlatform}
          data-cypress-id="mobile-blocker-back-to-platform-button"
          style={{
            display: "flex",
            minHeight: 40,
            maxHeight: 40,
            padding: "0 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            borderRadius: 8,
            border: "none",
            background: "#EE4360", // --Base-Primary-Default
            color: "#FFF",
            textAlign: "center",
            fontFeatureSettings: '"liga" off, "clig" off',
            fontFamily: FONT_STACK,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "120%",
            cursor: "pointer",
            boxSizing: "border-box",
          }}
        >
          Back to Oppizi OAds
        </button>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default MobileBlockerModal;
